import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { Swords, Play, Eye, Trophy, Clock, CheckCircle, AlertCircle, Globe } from "lucide-react";
import { LogIn, UserPlus } from "lucide-react";
import type { NegotiationCase } from "@shared/schema";
import { io as ioClient, type Socket } from "socket.io-client";

const API_BASE = "__PORT_5000__".startsWith("__") ? "" : "__PORT_5000__";

interface ExternalBot {
  id: string;
  name: string;
  callbackUrl: string;
  rating: number;
}

interface MatchMove {
  turn: number;
  botId: string;
  role: string;
  text: string;
  meta: any;
  timestamp: string;
  skipped: boolean;
}

interface ExternalMatch {
  id: string;
  botAId: string;
  botBId: string;
  mode: string;
  caseId: string;
  sandbox: boolean;
  status: string;
  moves: MatchMove[];
  winnerBotId: string | null;
  arbiterComment: string | null;
  scoreA: number;
  scoreB: number;
  createdAt: string;
}

function MatchStatusBadge({ status }: { status: string }) {
  switch (status) {
    case "pending": return <Badge variant="outline" className="text-xs gap-1"><Clock className="w-3 h-3" />Ожидание</Badge>;
    case "active": return <Badge className="text-xs gap-1 bg-blue-500/10 text-blue-400 border-blue-500/20"><Play className="w-3 h-3" />Идёт</Badge>;
    case "finished": return <Badge className="text-xs gap-1 bg-green-500/10 text-green-400 border-green-500/20"><CheckCircle className="w-3 h-3" />Завершён</Badge>;
    case "error": return <Badge variant="destructive" className="text-xs gap-1"><AlertCircle className="w-3 h-3" />Ошибка</Badge>;
    default: return <Badge variant="secondary" className="text-xs">{status}</Badge>;
  }
}

function MatchViewer({ matchId, allBots, onClose }: { matchId: string; allBots: ExternalBot[]; onClose: () => void }) {
  const [match, setMatch] = useState<ExternalMatch | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: initialMatch } = useQuery<ExternalMatch>({
    queryKey: ["/api/external-matches", matchId],
    staleTime: 0,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (initialMatch) setMatch(initialMatch);
  }, [initialMatch]);

  useEffect(() => {
    const socket = ioClient(API_BASE || window.location.origin, { path: "/socket.io" });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("join:match", matchId);
    });

    socket.on("match.move", (data: { matchId: string; move: MatchMove }) => {
      if (data.matchId === matchId) {
        setMatch(prev => prev ? { ...prev, moves: [...prev.moves, data.move], status: "active" } : prev);
      }
    });

    socket.on("match.finished", (data: any) => {
      if (data.matchId === matchId) {
        setMatch(prev => prev ? {
          ...prev,
          status: "finished",
          winnerBotId: data.winnerBotId,
          arbiterComment: data.comment,
          scoreA: data.scoreA,
          scoreB: data.scoreB,
        } : prev);
      }
    });

    return () => {
      socket.emit("leave:match", matchId);
      socket.disconnect();
    };
  }, [matchId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [match?.moves?.length]);

  if (!match) return <div className="p-4 text-muted-foreground">Загрузка...</div>;

  const botAName = allBots.find(b => b.id === match.botAId)?.name || match.botAId;
  const botBName = allBots.find(b => b.id === match.botBId)?.name || match.botBId;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-sm">{botAName}</span>
          <span className="text-xs text-muted-foreground">vs</span>
          <span className="font-semibold text-sm">{botBName}</span>
        </div>
        <MatchStatusBadge status={match.status} />
      </div>

      <ScrollArea className="h-[400px] pr-2" ref={scrollRef as any}>
        <div className="space-y-3">
          {match.moves.map((move, i) => (
            <div key={i} className={`flex ${move.role === "A" ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                move.role === "A"
                  ? "bg-blue-500/10 border border-blue-500/20"
                  : "bg-amber-500/10 border border-amber-500/20"
              } ${move.skipped ? "opacity-50 italic" : ""}`}>
                <div className="text-xs font-medium mb-1 text-muted-foreground">
                  {move.role === "A" ? botAName : botBName} — ход {move.turn}
                </div>
                <p>{move.text}</p>
                {move.meta?.tone && (
                  <div className="text-xs text-muted-foreground mt-1">Тон: {move.meta.tone}</div>
                )}
              </div>
            </div>
          ))}
          {match.moves.length === 0 && match.status === "active" && (
            <div className="text-center text-muted-foreground text-sm py-8">
              Ожидание ходов...
            </div>
          )}
        </div>
      </ScrollArea>

      {match.status === "finished" && (
        <Card className="p-4 bg-muted/30 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Trophy className="w-4 h-4 text-primary" />
            Результат
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span>{botAName}: <strong>{match.scoreA}</strong></span>
            <span>{botBName}: <strong>{match.scoreB}</strong></span>
          </div>
          {match.winnerBotId && (
            <p className="text-sm text-green-400">
              Победитель: {allBots.find(b => b.id === match.winnerBotId)?.name || match.winnerBotId}
            </p>
          )}
          {match.arbiterComment && (
            <p className="text-xs text-muted-foreground">{match.arbiterComment}</p>
          )}
        </Card>
      )}
    </div>
  );
}

export default function ExternalMatchesPage() {
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [viewMatchId, setViewMatchId] = useState<string | null>(null);

  const [botAId, setBotAId] = useState("");
  const [botBId, setBotBId] = useState("");
  const [caseId, setCaseId] = useState("");
  const [sandbox, setSandbox] = useState(true);

  // ALL hooks must be called before conditional returns
  const { data: allBots = [] } = useQuery<ExternalBot[]>({
    queryKey: ["/api/external-bots/all"],
    staleTime: 0,
    enabled: isAuthenticated,
  });

  const { data: cases = [] } = useQuery<NegotiationCase[]>({
    queryKey: ["/api/cases"],
    enabled: isAuthenticated,
  });

  const { data: matches = [], isLoading } = useQuery<ExternalMatch[]>({
    queryKey: ["/api/external-matches"],
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 5000,
    enabled: isAuthenticated,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/external-matches", {
        botAId, botBId, mode: "express", caseId, sandbox,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/external-matches"] });
      setCreateOpen(false);
      setBotAId(""); setBotBId(""); setCaseId("");
      setViewMatchId(data.id);
      toast({ title: "Матч создан" });
    },
    onError: (e: any) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  if (!isAuthenticated) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-xl font-semibold mb-2">Матчи внешних ботов</h1>
        <p className="text-sm text-muted-foreground mb-6">Войдите для управления матчами</p>
        <Card className="max-w-md mx-auto p-6 text-center text-muted-foreground">
          <Swords className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Требуется авторизация</p>
          <p className="text-xs mt-1">Перейдите в раздел «Внешние боты» для входа</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Матчи внешних ботов</h1>
          <p className="text-sm text-muted-foreground mt-1">Запускайте поединки между зарегистрированными ботами</p>
        </div>
        <Button size="sm" className="gap-2" onClick={() => setCreateOpen(true)} data-testid="button-create-match">
          <Swords className="w-4 h-4" /> Новый матч
        </Button>
      </div>

      {/* Create match dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Новый матч</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs mb-1 block">Бот A</Label>
              <Select value={botAId} onValueChange={setBotAId}>
                <SelectTrigger><SelectValue placeholder="Выберите бота A" /></SelectTrigger>
                <SelectContent>
                  {allBots.map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.rating} ELO)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Бот B</Label>
              <Select value={botBId} onValueChange={setBotBId}>
                <SelectTrigger><SelectValue placeholder="Выберите бота B" /></SelectTrigger>
                <SelectContent>
                  {allBots.filter(b => b.id !== botAId).map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.rating} ELO)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Кейс</Label>
              <Select value={caseId} onValueChange={setCaseId}>
                <SelectTrigger><SelectValue placeholder="Выберите кейс" /></SelectTrigger>
                <SelectContent>
                  {cases.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={sandbox} onCheckedChange={(v) => setSandbox(!!v)} />
              Sandbox-режим (тестовый)
            </label>
            <Button className="w-full" disabled={!botAId || !botBId || !caseId || createMutation.isPending}
              onClick={() => createMutation.mutate()}>
              {createMutation.isPending ? "Создаю..." : "Запустить матч"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Match viewer dialog */}
      <Dialog open={!!viewMatchId} onOpenChange={(o) => { if (!o) setViewMatchId(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Просмотр матча</DialogTitle></DialogHeader>
          {viewMatchId && <MatchViewer matchId={viewMatchId} allBots={allBots} onClose={() => setViewMatchId(null)} />}
        </DialogContent>
      </Dialog>

      {/* Match list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Card key={i} className="p-4 animate-pulse"><div className="h-4 bg-muted rounded w-1/3" /></Card>)}
        </div>
      ) : matches.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <Swords className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Матчей пока нет</p>
          <p className="text-xs mt-1">Создайте первый матч для тестирования ботов</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {matches.map(m => {
            const botA = allBots.find(b => b.id === m.botAId);
            const botB = allBots.find(b => b.id === m.botBId);
            const caseTitle = cases.find(c => c.id === m.caseId)?.title || m.caseId;

            return (
              <Card key={m.id} className="p-4 cursor-pointer hover:border-primary/20 transition-colors"
                onClick={() => setViewMatchId(m.id)} data-testid={`card-match-${m.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">{botA?.name || m.botAId}</span>
                    <span className="text-xs text-muted-foreground">vs</span>
                    <span className="text-sm font-medium">{botB?.name || m.botBId}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MatchStatusBadge status={m.status} />
                    {m.status === "finished" && (
                      <span className="text-xs text-muted-foreground">{m.scoreA} : {m.scoreB}</span>
                    )}
                    <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={(e) => { e.stopPropagation(); setViewMatchId(m.id); }}>
                      <Eye className="w-3 h-3" /> Просмотр
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <span>Кейс: {caseTitle}</span>
                  <span>•</span>
                  <span>{m.mode}</span>
                  <span>•</span>
                  <span>{m.moves.length} ходов</span>
                  {m.sandbox && <Badge variant="outline" className="text-xs">sandbox</Badge>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
